<template>
  <div class="w-full px-3.5 pt-3 pb-2.5">
    <canvas ref="chartCanvas"></canvas>
  </div>
</template>

<script>
import Chart from "chart.js/auto";

export default {
  name: "AiChart",

  props: {
    data:          { type: [Array, Object], default: () => ({}) },
    visualization: { type: Object, default: () => ({}) },
  },

  data() {
    return { chartInstance: null, _isMounted: false };
  },

  mounted() {
    this._isMounted = true;
    this.renderChart();
  },

  beforeUnmount() {
    this._isMounted = false;
    if (this.chartInstance) {
      this.chartInstance.destroy();
      this.chartInstance = null;
    }
  },

  watch: {
    visualization: { deep: true, handler() { this.renderChart(); } },
    data:          { deep: true, handler() { this.renderChart(); } },
  },

  methods: {
    renderChart() {
      if (!this._isMounted) return;
      if (this.chartInstance) {
        this.chartInstance.destroy();
        this.chartInstance = null;
      }
      const canvas = this.$refs.chartCanvas;
      if (!canvas) return;
      const ctx = canvas.getContext("2d");
      if (!ctx) return;

      const viz = this.visualization || {};
      let cfg;

      // ── Format E: normalized schema — flat data array + viz.series[{key,label}]
      if (viz.series?.length && viz.series[0]?.key !== undefined && Array.isArray(this.data) && this.data.length) {
        cfg = this.buildFromNormalizedSchema(viz, this.data);
      }
      // ── Format A: visualization.metrics [{label, value, color}] ────────────
      else if (viz.metrics?.length) {
        cfg = this.buildFromMetrics(viz);
      }
      // ── Format B: visualization.data_points [{name, value, color}] ─────────
      else if (viz.data_points?.length) {
        cfg = this.buildFromDataPoints(viz);
      }
      // ── Format C: visualization.data_series [{name, color, data[]}] ─────────
      else if (viz.data_series?.length) {
        cfg = this.buildFromDataSeries(viz);
      }
      // ── Format D: visualization.series [{name, data[], type, yaxis, color}] ─
      else if (viz.series?.length) {
        cfg = this.buildFromSeries(viz);
      }
      // ── Fallback: derive from data prop (time-series / generic arrays) ──────
      else {
        cfg = this.buildFromData(viz);
      }

      if (!cfg) return;

      const isHorizontal = viz.chart_type === 'horizontal_bar';
      const baseType = (viz.chart_type === 'line' || viz.chart_type === 'area') ? 'line'
        : (viz.chart_type === 'pie' || viz.chart_type === 'doughnut') ? viz.chart_type
        : 'bar';
      this.chartInstance = new Chart(ctx, {
        type: baseType,
        data: { labels: cfg.labels, datasets: cfg.datasets },
        options: this.buildOptions(viz, cfg.datasets.length, cfg.hasDualAxis, isHorizontal),
      });
    },

    // Format E ─ normalized backend schema: flat array + viz.series[{key,label}]
    buildFromNormalizedSchema(viz, data) {
      const isPie = viz.chart_type === 'pie' || viz.chart_type === 'doughnut';
      const isLine = viz.chart_type === 'line' || viz.chart_type === 'area';
      const isHorizontal = viz.chart_type === 'horizontal_bar';
      const palette = ['blue', 'purple', 'cyan', 'green', 'orange', 'red'];
      const alpha = isLine ? 0.15 : 0.7;

      // For horizontal_bar, x_axis.key is the VALUE axis — find the category field
      // (first data key that isn't used as a series value key).
      const seriesKeys = new Set(viz.series.map((s) => s.key));
      const xKey = isHorizontal
        ? (Object.keys(data[0] || {}).find((k) => !seriesKeys.has(k)) || 'label')
        : (viz.x_axis?.key || 'label');

      const labels = data.map((row) => row[xKey] ?? '');

      if (isPie) {
        const s = viz.series[0];
        return {
          labels,
          datasets: [{
            data: data.map((row) => this.toNum(row[s.key])),
            backgroundColor: palette.map((c) => this.toRgba(c, 0.8)),
            borderColor: '#fff',
            borderWidth: 2,
          }],
        };
      }

      return {
        labels,
        datasets: viz.series.map((s, i) => {
          const color = palette[i % palette.length];
          return {
            label: s.label,
            data: data.map((row) => this.toNum(row[s.key])),
            backgroundColor: this.toRgba(color, alpha),
            borderColor: this.toRgba(color, 1),
            borderWidth: 2,
            fill: viz.chart_type === 'area',
            tension: isLine ? 0.35 : 0,
            borderRadius: isLine ? 0 : 6,
            pointRadius: isLine ? 3 : 0,
            pointHoverRadius: isLine ? 5 : 0,
          };
        }),
      };
    },

    // Format A ─ kpi_card responses
    buildFromMetrics(viz) {
      return {
        labels: viz.metrics.map((m) => m.label),
        datasets: [{
          data:            viz.metrics.map((m) => m.value),
          backgroundColor: viz.metrics.map((m) => this.toRgba(m.color, 0.7)),
          borderColor:     viz.metrics.map((m) => this.toRgba(m.color, 1)),
          borderWidth: 2,
          borderRadius: 6,
        }],
      };
    },

    // Format B ─ text responses with threshold line
    buildFromDataPoints(viz) {
      const isPie = viz.chart_type === 'pie' || viz.chart_type === 'doughnut';
      if (isPie) {
        return {
          labels: viz.data_points.map((p) => p.name),
          datasets: [{
            data:            viz.data_points.map((p) => p.value),
            backgroundColor: viz.data_points.map((p) => this.toRgba(p.color || 'blue', 0.8)),
            borderColor:     '#fff',
            borderWidth:     2,
          }],
        };
      }

      const hasPct = String(viz.y_axis || "").includes("%");
      const datasets = [{
        type:            "bar",
        label:           viz.y_axis || "Value",
        data:            viz.data_points.map((p) => p.value),
        backgroundColor: viz.data_points.map((p) => this.toRgba(p.color || 'blue', 0.7)),
        borderColor:     viz.data_points.map((p) => this.toRgba(p.color || 'blue', 1)),
        borderWidth: 2,
        borderRadius: 6,
      }];

      if (viz.threshold_line !== undefined) {
        datasets.push({
          type:       "line",
          label:      `Avg ${viz.threshold_line}${hasPct ? "%" : ""}`,
          data:       viz.data_points.map(() => viz.threshold_line),
          borderColor:    "#6366f1",
          borderWidth:    2,
          borderDash:     [6, 4],
          pointRadius:    0,
          pointHoverRadius: 0,
          fill:       false,
          tension:    0,
          order:      0,
        });
      }

      return { labels: viz.data_points.map((p) => p.name), datasets };
    },

    // Format D ─ series with per-dataset type + optional dual y-axis
    buildFromSeries(viz) {
      const palette = ['blue', 'red', 'green', 'orange', 'purple', 'cyan'];
      const n = viz.series[0]?.data?.length || 0;
      // Generate hour labels (00:00–23:00) when the x_axis label suggests time
      const isHourly = /hour|heure/i.test(viz.x_axis || "");
      const labels = isHourly
        ? Array.from({ length: n }, (_, i) => `${String(i).padStart(2, "0")}:00`)
        : viz.categories || Array.from({ length: n }, (_, i) => String(i));

      const hasDualAxis = viz.series.some((s) => s.yaxis === "right");
      const datasets = viz.series.map((s, i) => {
        const isLine = s.type === "line" || s.type === "area";
        return {
          type: s.type === "area" ? "line" : (s.type || "bar"),
          label: s.name,
          data: s.data,
          backgroundColor: this.toRgba(s.color || palette[i % palette.length], isLine ? 0.15 : 0.7),
          borderColor: this.toRgba(s.color || palette[i % palette.length], 1),
          borderWidth: 2,
          borderRadius: isLine ? 0 : 4,
          fill: s.type === "area",
          tension: isLine ? 0.35 : 0,
          pointRadius: isLine ? 3 : 0,
          pointHoverRadius: isLine ? 5 : 0,
          yAxisID: hasDualAxis ? (s.yaxis === "right" ? "yRight" : "yLeft") : "y",
        };
      });
      return { labels, datasets, hasDualAxis };
    },

    // Format C ─ multi-series responses with categories
    buildFromDataSeries(viz) {
      const palette  = ['blue', 'red', 'green', 'orange', 'purple', 'cyan'];
      const isLine   = viz.chart_type === 'line' || viz.chart_type === 'area';
      return {
        labels: viz.categories || [],
        datasets: viz.data_series.map((s, i) => ({
          label:            s.name,
          data:             s.data,
          backgroundColor:  this.toRgba(s.color || palette[i % palette.length], isLine ? 0.15 : 0.7),
          borderColor:      this.toRgba(s.color || palette[i % palette.length], 1),
          borderWidth:      2,
          borderRadius:     isLine ? 0 : 6,
          fill:             false,
          tension:          isLine ? 0.35 : 0,
          pointRadius:      isLine ? 3 : 0,
          pointHoverRadius: isLine ? 5 : 0,
        })),
      };
    },

    // Fallback ─ generic chart/table response types (time-series etc.)
    buildFromData(viz) {
      const raw = Array.isArray(this.data) ? this.data : [this.data];
      if (!raw.length || !raw[0]) return null;

      const chartType = viz.chart_type || "bar";
      const xField    = typeof viz.x_axis === 'string' ? viz.x_axis
        : viz.x_axis?.key || this.guessXField(raw[0]);
      const yFields   = viz.y_axis
        ? [viz.y_axis].flat()
        : this.guessYFields(raw[0], xField);

      const palette = [
        { border: "#6366f1", bg: "rgba(99,102,241,0.15)" },
        { border: "#8b5cf6", bg: "rgba(139,92,246,0.15)" },
        { border: "#06b6d4", bg: "rgba(6,182,212,0.15)" },
        { border: "#10b981", bg: "rgba(16,185,129,0.15)" },
        { border: "#f59e0b", bg: "rgba(245,158,11,0.15)" },
        { border: "#ef4444", bg: "rgba(239,68,68,0.15)" },
      ];

      const isPie  = chartType === "pie" || chartType === "doughnut";
      const isArea = chartType === "area";

      return {
        labels: raw.map((row) => row[xField] ?? ""),
        datasets: yFields.map((field, idx) => {
          const c = palette[idx % palette.length];
          return {
            type:             isArea ? "line" : (isPie ? "pie" : "bar"),
            label:            field.replace(/_/g, " "),
            data:             raw.map((row) => this.toNum(row[field])),
            backgroundColor:  isPie ? palette.map((p) => p.border) : (isArea ? c.bg : c.bg),
            borderColor:      isPie ? "#fff" : c.border,
            borderWidth:      2,
            fill:             isArea,
            tension:          isArea ? 0.35 : 0,
            borderRadius:     isArea ? 0 : 6,
            pointRadius:      isArea ? 3 : undefined,
            pointHoverRadius: isArea ? 5 : undefined,
          };
        }),
      };
    },

    buildOptions(viz, datasetCount, hasDualAxis = false, isHorizontal = false) {
      const hasPct = String(viz.y_axis || "").includes("%");
      const rightLabel = viz.y_axis_right || "";
      const rightIsPct = rightLabel.includes("%");

      const xScale = {
        grid:   { display: false },
        ticks:  { font: { size: 11 }, color: "#94a3b8", maxRotation: 35 },
        border: { display: false },
      };
      const yBase = {
        beginAtZero: true,
        grid:   { color: "#f1f5f9", lineWidth: 1 },
        ticks:  { font: { size: 11 }, color: "#94a3b8" },
        border: { display: false },
      };

      const valueScaleTicks = { ...yBase.ticks, callback: (v) => hasPct ? `${v}%` : v };
      const scales = hasDualAxis
        ? {
            x: xScale,
            yLeft: {
              ...yBase,
              position: "left",
              title: viz.y_axis_left ? { display: true, text: viz.y_axis_left, font: { size: 10 }, color: "#94a3b8" } : undefined,
              ticks: { ...yBase.ticks, callback: (v) => hasPct ? `${v}%` : v },
            },
            yRight: {
              ...yBase,
              position: "right",
              grid: { drawOnChartArea: false },
              title: rightLabel ? { display: true, text: rightLabel, font: { size: 10 }, color: "#94a3b8" } : undefined,
              ticks: { ...yBase.ticks, callback: (v) => rightIsPct ? `${v}%` : v },
            },
          }
        : isHorizontal
          ? {
              // indexAxis:'y' swaps the axes — y is now the category axis, x is the value axis
              y: { ...xScale, ticks: { ...xScale.ticks, maxRotation: 0 } },
              x: { ...yBase, ticks: valueScaleTicks },
            }
          : {
              x: xScale,
              y: { ...yBase, ticks: valueScaleTicks },
            };

      return {
        responsive: true,
        maintainAspectRatio: true,
        ...(isHorizontal ? { indexAxis: 'y' } : {}),
        interaction: { mode: "index", intersect: false },
        plugins: {
          legend: {
            display: datasetCount > 1,
            position: "bottom",
            labels: {
              boxWidth: 10, boxHeight: 10, borderRadius: 3,
              padding: 12, font: { size: 11 }, color: "#64748b",
            },
          },
          tooltip: {
            backgroundColor: "#1e293b",
            titleFont: { size: 12 }, bodyFont: { size: 12 },
            padding: 10, cornerRadius: 8,
            titleColor: "#f8fafc", bodyColor: "#cbd5e1",
            callbacks: {
              label: (ctx) => {
                const ds = ctx.dataset;
                const isPct = hasDualAxis ? ds.yAxisID === "yRight" && rightIsPct : hasPct;
                return ` ${ctx.raw}${isPct ? "%" : ""}`;
              },
            },
          },
        },
        scales,
      };
    },

    // ── Helpers ───────────────────────────────────────────────────────────────

    toRgba(color, alpha) {
      const named = {
        red:    [239, 68,  68],
        orange: [245, 158, 11],
        green:  [16,  185, 129],
        blue:   [99,  102, 241],
        yellow: [234, 179, 8],
        purple: [139, 92,  246],
        cyan:   [6,   182, 212],
        pink:   [236, 72,  153],
      };
      if (named[color]) {
        const [r, g, b] = named[color];
        return `rgba(${r},${g},${b},${alpha})`;
      }
      if (typeof color === "string" && color.startsWith("#")) {
        const hex = color.replace("#", "");
        const r   = parseInt(hex.slice(0, 2), 16);
        const g   = parseInt(hex.slice(2, 4), 16);
        const b   = parseInt(hex.slice(4, 6), 16);
        return `rgba(${r},${g},${b},${alpha})`;
      }
      return color;
    },

    toNum(val) {
      if (typeof val === "number") return val;
      const n = parseFloat(val);
      return isNaN(n) ? 0 : n;
    },

    guessXField(sample) {
      const timeKeys = ["date", "day", "hour", "week", "month", "weekday", "quarter_hour", "time", "period"];
      for (const k of timeKeys) { if (k in sample) return k; }
      return Object.keys(sample)[0];
    },

    guessYFields(sample, xField) {
      return Object.keys(sample)
        .filter((k) => {
          if (k === xField) return false;
          const v = sample[k];
          return typeof v === "number" || (typeof v === "string" && !isNaN(Number(v)));
        })
        .slice(0, 5);
    },
  },
};
</script>
